import {
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    DropZone,
    FormLayout,
    InlineStack,
    Layout,
    Modal,
    Page,
    ProgressBar,
    Select,
    Text,
    TextField,
    Thumbnail,
} from '@shopify/polaris';
import { NoteIcon } from '@shopify/polaris-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import agent, { AcesImportSetup, FitmentImportHistoryItem, ImportFormat } from '../../app/api/agent';
import ImportHistoryCards from './ImportHistoryCards';

type UploadState = {
    file?: File;
    error?: string;
    success?: string;
};

type AcesSetupState = {
    queueId: number;
    details: AcesImportSetup;
    selectedVendor: string;
    skuSuffixRule: string;
    useSkuSuffixRule: boolean;
    updateVendorRule: boolean;
    normalizeSku: boolean;
};

type VendorSkuRuleSettings = {
    skuPrefix: string;
    normalizeSku: boolean;
};

const noVendorOptionValue = '__NO_VENDOR__';

const formatOptions = [
    { label: 'CSV/XLSX', value: 'csv_xlsx' },
    { label: 'ACES XML', value: 'aces_xml' },
];

const ImportsPage = () => {
    const [importFormat, setImportFormat] = useState<ImportFormat>('csv_xlsx');
    const [uploadState, setUploadState] = useState<UploadState>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgressPercent, setUploadProgressPercent] = useState<number | undefined>();
    const [history, setHistory] = useState<FitmentImportHistoryItem[]>([]);
    const [historyError, setHistoryError] = useState<string | undefined>();
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [archivingIds, setArchivingIds] = useState<Record<number, boolean>>({});
    const [downloadingIds, setDownloadingIds] = useState<Record<number, boolean>>({});
    const [isConfirmingAcesSetup, setIsConfirmingAcesSetup] = useState(false);
    const [acesSetup, setAcesSetup] = useState<AcesSetupState | undefined>();
    const [vendorSuffixRulesByVendor, setVendorSuffixRulesByVendor] = useState<Record<string, VendorSkuRuleSettings>>({});

    const isValidFileForFormat = useCallback((file: File, format: ImportFormat) => {
        const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : '';

        if (format === 'csv_xlsx') {
            return extension === 'csv' || extension === 'xlsx';
        }

        return extension === 'xml';
    }, []);

    const validationHint = useMemo(() => {
        return importFormat === 'csv_xlsx'
            ? 'Accepted file types: .csv, .xlsx'
            : 'Accepted file types: .xml';
    }, [importFormat]);

    const loadHistory = useCallback(async () => {
        try {
            setIsLoadingHistory(true);
            setHistoryError(undefined);
            const result = await agent.Imports.history();
            setHistory(result);
        } catch (error: any) {
            const message = error?.response?.data || error?.message || 'Failed to load import history.';
            setHistoryError(String(message));
        } finally {
            setIsLoadingHistory(false);
        }
    }, []);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const loadVendorSuffixRules = useCallback(async () => {
        try {
            const rules = await agent.Settings.vendorSkuRules();
            const mappedRules = rules.reduce<Record<string, VendorSkuRuleSettings>>((accumulator, rule) => {
                accumulator[rule.shopifyVendor] = {
                    skuPrefix: rule.skuPrefix || '',
                    normalizeSku: rule.normalizeSku === true,
                };
                return accumulator;
            }, {});

            setVendorSuffixRulesByVendor(mappedRules);
        } catch {
            setVendorSuffixRulesByVendor({});
        }
    }, []);

    useEffect(() => {
        if (!acesSetup?.queueId) {
            return;
        }

        loadVendorSuffixRules();
    }, [acesSetup?.queueId, loadVendorSuffixRules]);

    useEffect(() => {
        if (!acesSetup?.selectedVendor) {
            return;
        }

        const vendorRule = vendorSuffixRulesByVendor[acesSetup.selectedVendor];

        if (vendorRule === undefined) {
            return;
        }

        setAcesSetup((previous) => {
            if (!previous || previous.selectedVendor !== acesSetup.selectedVendor || (previous.skuSuffixRule === vendorRule.skuPrefix && previous.normalizeSku === vendorRule.normalizeSku)) {
                return previous;
            }

            return {
                ...previous,
                skuSuffixRule: vendorRule.skuPrefix,
                normalizeSku: vendorRule.normalizeSku,
            };
        });
    }, [acesSetup?.selectedVendor, vendorSuffixRulesByVendor]);

    const handleDropZoneDrop = useCallback((_: File[], acceptedFiles: File[]) => {
        setUploadProgressPercent(undefined);
        const selectedFile = acceptedFiles[0];

        if (!selectedFile) {
            setUploadState({ file: undefined, error: 'No file selected.' });
            return;
        }

        if (!isValidFileForFormat(selectedFile, importFormat)) {
            setUploadState({
                file: undefined,
                success: undefined,
                error: importFormat === 'csv_xlsx'
                    ? 'Invalid file type. Select a .csv or .xlsx file.'
                    : 'Invalid file type. Select a .xml file.'
            });
            return;
        }

        setUploadState({ file: selectedFile, error: undefined, success: undefined });
    }, [importFormat, isValidFileForFormat]);

    const handleFormatChange = useCallback((value: string) => {
        const normalized = value as ImportFormat;
        setImportFormat(normalized);
        setAcesSetup(undefined);
        setUploadProgressPercent(undefined);

        setUploadState((prev) => {
            if (!prev.file) {
                return { ...prev, error: undefined };
            }

            if (isValidFileForFormat(prev.file, normalized)) {
                return { ...prev, error: undefined };
            }

            return {
                file: undefined,
                success: undefined,
                error: normalized === 'csv_xlsx'
                    ? 'Current file does not match CSV/XLSX format. Please select a .csv or .xlsx file.'
                    : 'Current file does not match ACES XML format. Please select a .xml file.'
            };
        });
    }, [isValidFileForFormat]);

    const handleSubmit = useCallback(async () => {
        if (!uploadState.file) {
            setUploadProgressPercent(undefined);
            setUploadState((prev) => ({ ...prev, error: 'Please select a file to upload.' }));
            return;
        }

        if (!isValidFileForFormat(uploadState.file, importFormat)) {
            setUploadProgressPercent(undefined);
            setUploadState((prev) => ({
                ...prev,
                error: importFormat === 'csv_xlsx'
                    ? 'Invalid file type for CSV/XLSX. Use .csv or .xlsx.'
                    : 'Invalid file type for ACES XML. Use .xml.'
            }));
            return;
        }

        try {
            setIsSubmitting(true);
            setUploadProgressPercent(0);
            const result = await agent.Imports.queue(uploadState.file, importFormat, (progressEvent) => {
                if (!progressEvent.total || progressEvent.total <= 0) {
                    return;
                }

                const percent = Math.min(100, Math.round((progressEvent.loaded / progressEvent.total) * 100));
                setUploadProgressPercent(percent);
            });

            if (importFormat === 'aces_xml' && result.acesSetup) {
                const details = result.acesSetup;
                setAcesSetup({
                    queueId: result.queueId,
                    details,
                    selectedVendor: details.matchedVendor || '',
                    skuSuffixRule: details.vendorSkuSuffixRule || '',
                    useSkuSuffixRule: true,
                    updateVendorRule: true,
                    normalizeSku: details.normalizeSku === true,
                });

                setUploadState({
                    file: undefined,
                    error: undefined,
                    success: 'ACES header parsed. Review vendor and SKU mapping settings, then start the import.'
                });
            } else {
                setUploadState({
                    file: undefined,
                    error: undefined,
                    success: 'Your import has been scheduled for processing.'
                });
            }

            await loadHistory();
        } catch (error: any) {
            const message = error?.response?.data || error?.message || 'Failed to queue import.';
            setUploadState((prev) => ({ ...prev, success: undefined, error: String(message) }));
        } finally {
            setIsSubmitting(false);
            setUploadProgressPercent(undefined);
        }
    }, [importFormat, isValidFileForFormat, loadHistory, uploadState.file]);

    const handleConfirmAcesSetup = useCallback(async () => {
        if (!acesSetup) {
            return;
        }

        try {
            setIsConfirmingAcesSetup(true);
            const hasVendorSelection = !!acesSetup.selectedVendor;
            await agent.Imports.configureAces(acesSetup.queueId, {
                selectedVendor: hasVendorSelection ? acesSetup.selectedVendor : undefined,
                skuSuffixRule: hasVendorSelection ? acesSetup.skuSuffixRule : undefined,
                useSkuSuffixRule: hasVendorSelection && acesSetup.useSkuSuffixRule,
                updateVendorRule: hasVendorSelection && acesSetup.updateVendorRule,
                normalizeSku: hasVendorSelection && acesSetup.normalizeSku,
            });

            setUploadState({ file: undefined, error: undefined, success: 'ACES import configured and queued for processing.' });
            setAcesSetup(undefined);
            setUploadProgressPercent(undefined);
            await loadHistory();
        } catch (error: any) {
            const message = error?.response?.data || error?.message || 'Failed to configure ACES import.';
            setUploadState((prev) => ({ ...prev, success: undefined, error: String(message) }));
        } finally {
            setIsConfirmingAcesSetup(false);
        }
    }, [acesSetup, loadHistory]);

    const handleCloseAcesModal = useCallback(() => {
        if (isConfirmingAcesSetup) {
            return;
        }

        setAcesSetup(undefined);
        setUploadProgressPercent(undefined);
    }, [isConfirmingAcesSetup]);

    const handleArchive = useCallback(async (queueId: number) => {
        try {
            setArchivingIds((prev) => ({ ...prev, [queueId]: true }));
            await agent.Imports.archive(queueId);
            setHistory((prev) => prev.filter((item) => item.queueId !== queueId));
        } catch (error: any) {
            const message = error?.response?.data || error?.message || 'Failed to archive import history item.';
            setHistoryError(String(message));
        } finally {
            setArchivingIds((prev) => ({ ...prev, [queueId]: false }));
        }
    }, []);

    const handleDownload = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.download(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = item.originalFileName || `import-${item.queueId}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            const message = error?.response?.data || error?.message || 'Failed to download import file.';
            setHistoryError(String(message));
        } finally {
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const fileUpload = !uploadState.file && <DropZone.FileUpload actionHint={validationHint} />;
    const uploadedFile = uploadState.file && (
        <InlineStack gap="300" align="start" blockAlign="center">
            <Thumbnail
                size="small"
                alt={uploadState.file.name}
                source={NoteIcon}
            />
            <div>
                <Text as="p" variant="bodyMd">{uploadState.file.name}</Text>
                <Text as="p" variant="bodySm" tone="subdued">{uploadState.file.size} bytes</Text>
            </div>
        </InlineStack>
    );

    return (
        <Page title="Imports">
            <Layout>
                <Layout.Section>
                    <Card>
                        <FormLayout>
                            <Select
                                label="Import format"
                                options={formatOptions}
                                onChange={handleFormatChange}
                                value={importFormat}
                            />

                            <DropZone allowMultiple={false} onDrop={handleDropZoneDrop}>
                                {uploadedFile}
                                {fileUpload}
                            </DropZone>

                            {uploadState.error && (
                                <Banner tone="critical" title="Upload validation failed">
                                    <p>{uploadState.error}</p>
                                </Banner>
                            )}

                            {uploadState.success && (
                                <Banner tone="success" title="Import queued">
                                    <p>{uploadState.success}</p>
                                </Banner>
                            )}

                            {isSubmitting && uploadProgressPercent !== undefined && (
                                <BlockStack gap="100">
                                    <Text as="p" variant="bodySm" tone="subdued">Uploading file… {uploadProgressPercent}%</Text>
                                    <ProgressBar progress={uploadProgressPercent} size="small" />
                                </BlockStack>
                            )}

                            <InlineStack align="end">
                                <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
                                    Submit File
                                </Button>
                            </InlineStack>
                        </FormLayout>
                    </Card>
                </Layout.Section>

                <Layout.Section>
                    <Card>
                        <BlockStack gap="300">
                            <InlineStack align="space-between">
                                <Text as="h2" variant="headingMd">Import history</Text>
                                <Button onClick={loadHistory} loading={isLoadingHistory}>Refresh</Button>
                            </InlineStack>

                            {historyError && (
                                <Banner tone="critical" title="Unable to load import history">
                                    <p>{historyError}</p>
                                </Banner>
                            )}

                            {history.length === 0 && !isLoadingHistory ? (
                                <Text as="p" tone="subdued">No import history yet.</Text>
                            ) : (
                                <BlockStack gap="200">
                                    <ImportHistoryCards
                                        items={history.slice(0, 5)}
                                        archivingIds={archivingIds}
                                        downloadingIds={downloadingIds}
                                        onArchive={handleArchive}
                                        onDownload={handleDownload}
                                    />
                                    <Box paddingBlockStart="200">
                                        <InlineStack align="center">
                                            <Link to="/imports/history">View all imports</Link>
                                        </InlineStack>
                                    </Box>
                                </BlockStack>
                            )}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>

            <Modal
                open={!!acesSetup}
                onClose={handleCloseAcesModal}
                title="Finalize ACES import"
                primaryAction={{
                    content: 'Start ACES import',
                    onAction: handleConfirmAcesSetup,
                    loading: isConfirmingAcesSetup,
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: handleCloseAcesModal,
                    },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="300">
                        <Text as="p" tone="subdued">Confirm vendor mapping before fitment rows are processed.</Text>

                        <Text as="p" variant="bodySm">{acesSetup?.details.documentTitle || 'Untitled ACES document'} {acesSetup?.details.company ? `· ${acesSetup.details.company}` : ''}</Text>

                        <Select
                            label="Vendor (optional)"
                            options={[
                                { label: 'No vendor (match store-wide)', value: noVendorOptionValue },
                                ...(acesSetup?.details.vendorOptions.map((v) => ({ label: v, value: v })) ?? []),
                            ]}
                            value={acesSetup?.selectedVendor || noVendorOptionValue}
                            onChange={(value) => setAcesSetup((previous) => {
                                if (!previous) {
                                    return previous;
                                }

                                const selectedVendor = value === noVendorOptionValue ? '' : value;

                                return {
                                    ...previous,
                                    selectedVendor,
                                    skuSuffixRule: selectedVendor ? (vendorSuffixRulesByVendor[selectedVendor]?.skuPrefix ?? '') : '',
                                    useSkuSuffixRule: selectedVendor ? previous.useSkuSuffixRule : false,
                                    updateVendorRule: selectedVendor ? previous.updateVendorRule : false,
                                    normalizeSku: selectedVendor ? (vendorSuffixRulesByVendor[selectedVendor]?.normalizeSku ?? false) : false,
                                };
                            })}
                            helpText="Leave vendor unselected to match SKUs across the entire store."
                        />

                        <TextField
                            label="Vendor SKU suffix rule"
                            value={acesSetup?.skuSuffixRule ?? ''}
                            onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, skuSuffixRule: value } : previous)}
                            autoComplete="off"
                            disabled={!acesSetup?.selectedVendor}
                            helpText="Adds this suffix when matching ACES part numbers to Shopify SKUs. Falls back to the original part number if no match is found."
                        />

                        <Checkbox
                            label="Use this suffix rule for this import"
                            checked={acesSetup?.useSkuSuffixRule ?? false}
                            onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, useSkuSuffixRule: value } : previous)}
                            disabled={!acesSetup?.selectedVendor}
                        />

                        <Checkbox
                            label="Normalize SKU (remove special characters before matching)"
                            checked={acesSetup?.normalizeSku ?? false}
                            onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, normalizeSku: value } : previous)}
                            disabled={!acesSetup?.selectedVendor}
                        />

                        <Checkbox
                            label="Update saved vendor rule with this value"
                            checked={acesSetup?.updateVendorRule ?? false}
                            onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, updateVendorRule: value } : previous)}
                            disabled={!acesSetup?.selectedVendor}
                        />
                    </BlockStack>
                </Modal.Section>
            </Modal>
        </Page>
    );
};

export default ImportsPage;
